import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Typography, Checkbox } from "@mui/material";

// Modal Components
import CustomModal from "../../../../modal/CustomModal";
import IngredientModal from "../modals/IngredientModal";

// Utility Functions
import { decimalToFraction, convertToTitleCase } from "../../../../../utils/recipe";

// Actions
import { setIngredientMeasured } from "../../../../../actions/walkthrough";

// String formatting
const formatAmountUnit = (ingredient) => {
  const unit = ingredient.unit.display;
  let formattedAmount = "";
  let formattedUnit = "";

  if (unit && unit.magnitude && unit.magnitude !== "") {
    formattedAmount = decimalToFraction(unit.magnitude);
  }

  if (unit && unit.text !== "" && unit.text !== "not_exact" && unit.text !== "self" && unit.type !== "unknown") {
    formattedUnit = unit.text;
  }

  let showSpace = false;

  if (formattedAmount !== "" && formattedUnit !== "") {
    showSpace = true;
  }

  return `${formattedAmount} ${formattedUnit}${showSpace ? " " : ""}`;
};

const formatIngredient = (ingredient) => {
  let formattedName =
    ingredient.name && ingredient.name !== "" ? convertToTitleCase(ingredient.name) : "Unknown Ingredient";

  return `${formattedName}`;
};

const formatState = (ingredient) => {
  let formattedState = "";

  if (ingredient.state && ingredient.state !== "") {
    formattedState = convertToTitleCase(ingredient.state);
  }

  if (formattedState !== "") {
    return `, ${formattedState}`;
  }
};

const Ingredient = ({
  section,
  sectionIndex,
  ingredient,
  index,
  errors,
  setIngredientMeasured,
  useCheckboxMarker = false,
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleMeasuredToggle = () => {
    setIngredientMeasured(sectionIndex, index, !ingredient.measured);
  };

  const topLeftAdornment = (
    <div className="flex flex-row items-center">
      <Checkbox checked={ingredient.measured} onChange={handleMeasuredToggle} size="small" />
      <div>
        <Typography variant="caption" className="text-secondaryText-600">
          I used this
        </Typography>
      </div>
    </div>
  );

  return (
    <div>
      <CustomModal open={showModal} handleClose={handleCloseModal} type="bottom" topLeftAdornment={topLeftAdornment}>
        <IngredientModal
          section={section}
          sectionIndex={sectionIndex}
          ingredient={ingredient}
          index={index}
          handleClose={handleCloseModal}
        />
      </CustomModal>

      <li
        className={`my-2 cursor-pointer ${
          useCheckboxMarker
            ? "flex items-start gap-2"
            : ingredient.measured
            ? "marker:text-mainGreen list-disc"
            : "marker:text-secondaryText-600 list-disc"
        }`}
        onClick={handleOpenModal}
      >
        {useCheckboxMarker && (
          <Checkbox
            checked={ingredient.measured}
            onClick={(e) => {
              e.stopPropagation(); // prevent modal open
              handleMeasuredToggle();
            }}
            size="small"
            className="px-0 py-1 mr-2"
          />
        )}

        <div>
          <Typography variant="subtitle1" className="font-medium">
            <span className={`font-normal ${ingredient.measured ? "text-primaryText" : "text-secondaryText"}`}>
              {formatAmountUnit(ingredient)}
            </span>
            <span className={`${ingredient.measured ? "text-primaryText" : "text-secondaryText-600"}`}>
              {formatIngredient(ingredient)}
            </span>
            {ingredient.state !== "" && (
              <span className="font-normal italic text-secondaryText">{formatState(ingredient)}</span>
            )}
          </Typography>
          {ingredient.note && ingredient.note !== "" && (
            <Typography variant="body2" className="text-secondaryText">
              ({ingredient.note})
            </Typography>
          )}
        </div>
      </li>
    </div>
  );
};

Ingredient.propTypes = {
  section: PropTypes.object.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  ingredient: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  errors: PropTypes.object.isRequired,
  setIngredientMeasured: PropTypes.func.isRequired,
  useCheckboxMarker: PropTypes.bool,
};

export default connect(null, { setIngredientMeasured })(Ingredient);
